<script setup lang="ts">
/**
 * Rise through the ranks: the fifteen rank badges on a ladder that fills as
 * the visitor scrolls, a captain beside it who ages from junior to senior to
 * flag rank as the rungs light (`CaptainCard.vue`), and the four season medals
 * under it (`MedalRow.vue`).
 *
 * The fill is `scroll-progress.ts`; which rung is lit is `landing-content.ts`.
 * Under reduced motion the progress is 1 at rest: every rung lit, the flag
 * captain showing.
 */
import { computed, ref } from 'vue';

import { RANKS, rankNameKey } from '@shared/ranks';

import { useI18n } from '@/i18n';
import GameIcon from '@/ui/kit/GameIcon.vue';
import { rankArt } from '@/ui/panel/art-path';

import { ranksReached } from '../landing-content';
import { useLandingScroller } from '../scroller';
import { useScrollProgress } from '../scroll-progress';
import CaptainCard from './CaptainCard.vue';
import MedalRow from './MedalRow.vue';
import SectionShell from './SectionShell.vue';

const { t } = useI18n();

/** The ladder's own box is measured: it starts filling as its top climbs past 80% of the screen and is full as its foot passes 50%. */
const FILL_START = 0.8;
const FILL_END = 0.5;

const ladderEl = ref<HTMLElement | null>(null);
const progress = useScrollProgress(useLandingScroller(), () => ladderEl.value, FILL_START, FILL_END);

const reached = computed(() => ranksReached(progress.value));
/** The rung the captain has got to: the last lit one, never below the first. */
const current = computed(() => Math.max(0, reached.value - 1));
const fmt = (n: number): string => n.toLocaleString('en-GB');
</script>

<template>
  <SectionShell id="ranks" icon="medal" :title="t('landing.ranks.title')" :line="t('landing.ranks.line')" backdrop="/art/scenes/promotion.webp">
    <div class="layout">
      <CaptainCard :rank-index="current" data-slam="deal" />

      <div class="ladder-wrap">
        <ol ref="ladderEl" class="ladder" :style="{ '--fill': progress }">
          <li v-for="r in RANKS" :key="r.id" class="rung k-well" :class="{ lit: r.index < reached, now: r.index === current && reached > 0 }">
            <img class="rung-badge" :src="rankArt(r.id)" alt="" width="56" height="56" loading="lazy" decoding="async" />
            <span class="k-label rung-name">{{ t(rankNameKey(r.id)) }}</span>
            <span class="k-chip rung-xp"><GameIcon name="star" :size="24" />{{ t('landing.ranks.xp', { xp: fmt(r.minXp) }) }}</span>
          </li>
        </ol>
      </div>
    </div>

    <MedalRow />
  </SectionShell>
</template>

<style scoped>
.layout {
  display: grid;
  gap: 18px;
  max-width: 980px;
  margin: 0 auto;
}

.ladder-wrap {
  position: relative;
}

/* The rail the fill climbs, drawn as the kit's progress groove. */
.ladder {
  position: relative;
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0 0 0 34px;
  list-style: none;
}

.ladder::before,
.ladder::after {
  content: '';
  position: absolute;
  left: 8px;
  top: 4px;
  bottom: 4px;
  width: 14px;
  border-radius: 999px;
}

.ladder::before {
  background: #070c12;
  box-shadow: inset 0 2px 3px rgba(0, 0, 0, 0.8), 0 0 0 2px #2c3b4c;
}

.ladder::after {
  background: linear-gradient(0deg, #c47a12, #f0a92a 45%, #ffe08a);
  transform-origin: top;
  transform: scaleY(var(--fill, 1));
  left: 10px;
  top: 6px;
  bottom: 6px;
  width: 10px;
  transition: transform 300ms var(--ease);
}

.rung {
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  min-height: 68px;
  padding: 6px 12px 6px 6px;
  filter: grayscale(1) brightness(0.55);
  transition: filter 400ms var(--ease);
}

.rung.lit {
  filter: none;
}

.rung.now {
  box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.55), 0 0 0 2px var(--kit-gold);
}

.rung.lit .rung-badge {
  animation: rung-slam 420ms cubic-bezier(0.2, 1.6, 0.4, 1) both;
}

@keyframes rung-slam {
  from {
    transform: scale(1.5) rotate(-8deg);
  }
}

.rung-badge {
  width: 56px;
  height: 56px;
  border-radius: 22%;
  box-shadow: 0 0 0 2px #0d141c;
}

.rung-name {
  font-size: 1.05rem;
  text-transform: uppercase;
}

.rung-xp {
  font-size: 0.95rem;
}

@media (min-width: 820px) {
  .layout {
    grid-template-columns: 300px minmax(0, 1fr);
    align-items: start;
    gap: 32px;
  }
}
</style>
